import {CF} from 'module/CustomField/util/CustomField';
import {CFAttrKey} from 'module/CustomField/constant/CFAttrConstants'
import {CustomFieldTO, KontStammTO, StTagTO} from 'generated/JavaTO'
import {CFModel, CFValue} from 'module/CustomField/type/CFType'
import {StateObservable} from 'redux-observable'
import {StTagZu} from 'generated/JavaTOmodifications'
import {selBSCollectionByIdDict, selBSCollectionOwnerDateTimestamps} from 'module/BSCollection/selector/BSCollectionSelector'
import {SisName} from 'util/SisName'
import {selStTagById} from 'module/StTag/selector/StTagSelector'
import {isEmpty, isEqual} from 'lodash'
import {combineLatestWith, forkJoin, from, Observable, of, race} from 'rxjs'
import {PayloadAction} from 'util/redux'
import {stTagEnsureRefreshedAction, stTagResetAction} from 'module/StTag/action/StTagAction'
import moment from 'moment'
import {catchError, filter, map, mergeMap, take} from 'rxjs/operators'
import {bsFetchCollectionAction, bsMergeResponseAction, bsRefreshCollectionAction, bsSkipFetchCollectionAction} from 'module/BSCollection/action/BSCollectionAction'
import {SisNameBS} from 'module/BSCollection/type/SisNameBS'
import {selKontStammByPersNr} from 'module/KontStamm/KontStammSelector'
import {kontStammFetchAction, kontStammResponseAction} from 'module/KontStamm/KontStammAction'
import {RequiredKeys} from 'util/types/KeyTypeUtil'
import {ClipboardMap} from 'module/Clipboard/type/ClipboardTypes'
import {InputType} from "module/CustomField/util/CFInputType";
import {selKostFetched} from "module/Kost/selector/KostSelector";
import {ensureKostsFetchedAction} from "module/Kost/action/KostAction";
import {parseSisDate} from 'util/SisDateTime'
import {selClipboardMap} from "module/Clipboard/selector/ClipboardSelector";
import {selUrlParamsModel} from "module/UrlParam/selector/UrlParamSelector";
import {searchDq} from 'module/Dq/searchDq'
import {getDqParamsObject, parseDqParamFromSerializedString} from 'module/CustomField/util/DqParam'
import {log} from 'common/log'

export interface CFDefModelInitContext {
	stTagZu?: StTagZu
	stTag?: StTagTO
	kontStamm?: KontStammTO
	clipboard?: ClipboardMap
	urlParams?: Partial<CFModel>
	initValueFromDqMap?: { [cfDefId in number]: CFValue[] }
}

const selStTagZuById = selBSCollectionByIdDict[SisName.StTagZu]
const selStTagZuTSMap = selBSCollectionOwnerDateTimestamps[SisName.StTagZu]

interface ParamsBase {
	model: CFModel
	cfDefs: CustomFieldTO[]
	persNr?: number
	date?: number
}

// metaData is any data that is needed afterwards in result mapper that can be used accustomed to the implementation
type Params<T> = T extends void ? ParamsBase : ParamsBase & { metaData: T }

type Result<T> = Params<T> & { context?: CFDefModelInitContext }

function getRequestParams<T>(params: RequiredKeys<Params<T>, "persNr" | "date">[]) {
	const persNrs = [...new Set(params.map(x => x.persNr))].sort()
	const dates = [...new Set(params.map(x => x.date))]
	const startDate = Math.min(...dates)
	const endDate = Math.max(...dates)
	return {persNrs, startDate, endDate}
}

function isInitValueFromDqResult(x: any): x is { paramsIndex: number, initValueFromDq: { [cfDefId in number]: CFValue[] } } {
	return x && x.initValueFromDq && (x.paramsIndex || x.paramsIndex === 0)
}

export function getCFDefModelInitContextObservable<T = void>(
	action$: Observable<PayloadAction<any, any>>,
	state$: StateObservable<any>,
	params: Params<T>[],
): [Observable<PayloadAction<any, any>>, Observable<Result<T>[]>] {
	const depActions: PayloadAction<any, any>[] = []
	const resolvers: Observable<any>[] = [of('ensure resolvers emit a value, even if empty')]
	const paramsPD = params.filter(x => x.persNr && x.date) as RequiredKeys<Params<T>, 'persNr' | 'date'>[]
	const minTS = +moment().add(-1, 'd')

	// visible kost input
	// does not need resolver, but requires kosts fetched for input
	if (!selKostFetched(state$.value) && params.some(p => p.cfDefs?.some(cfDef => cfDef.input === InputType.Kost))) {
		depActions.push(ensureKostsFetchedAction())
	}

	// stammKostMap
	const kontStammByPersNr = selKontStammByPersNr(state$.value)
	const pKontStamm = paramsPD.filter(p =>
		CF.hasCFAttr(p.cfDefs, CFAttrKey.initFromStammKost) &&
		// has not been fetched in the last <minTS>
		!kontStammByPersNr[p.persNr]?.fetchTS.some(f => p.date && p.date >= f.start && p.date <= f.end && f.ts > minTS),
	)
	if (pKontStamm.length) {
		const p = getRequestParams(pKontStamm)
		const kontStammParams = {...p, force: true}
		depActions.push(kontStammFetchAction(kontStammParams))
		resolvers.push(action$.pipe(
			filter(action => kontStammResponseAction.match(action) && isEqual(kontStammParams, action.payload.params)),
			take(1),
		))
	}

	//stTagZu
	if (state$.value.BSCollection && state$.value.StTag) {
		const stTagZuTS = selStTagZuTSMap(state$.value)
		const pStTagZu = paramsPD.filter(p => CF.hasCFAttr(p.cfDefs, CFAttrKey.initValueFromStTag) && !(stTagZuTS[p.persNr]?.[p.date] > minTS))
		if (pStTagZu.length) {
			const {persNrs, startDate, endDate} = getRequestParams(pStTagZu)
			const payload = {
				sisName: SisName.StTagZu as SisNameBS,
				overrideOwners: persNrs,
				from: +parseSisDate(startDate),
				to: +parseSisDate(endDate).endOf('day'),
				force: false,
			}
			depActions.push(bsRefreshCollectionAction(payload))
			resolvers.push(
				race(
					action$.pipe(filter(action => bsSkipFetchCollectionAction.match(action) && isEqual(action.payload, payload))),
					action$.pipe(
						filter(action => bsFetchCollectionAction.match(action) && action.payload.sisName === SisName.StTagZu),
						combineLatestWith(
							action$.pipe(
								filter(action => bsMergeResponseAction.match(action) && action.payload.sisName === SisName.StTagZu),
							),
						),
					),
				).pipe(
					take(1),
				),
			)
		}

		//stTag
		const stTagById = selStTagById(state$.value)
		if (isEmpty(stTagById) && pStTagZu.length) {
			depActions.push(stTagEnsureRefreshedAction())
			resolvers.push(action$.pipe(filter(stTagResetAction.match), take(1)))
		}
	}

	//initFromDqMap
	params.forEach(({model, cfDefs}, i) => {
		if (!model || !cfDefs) return; // skip
		cfDefs.forEach(cfDef => {
			const dqkey = CF.getCFAttr(cfDef, CFAttrKey.initValueFromDq)?.value
			if (!dqkey) return;
			const dqParams = cfDef.cfAttrs.flatMap(cfAttr => cfAttr.key === CFAttrKey.initValueFromDqParam ? [parseDqParamFromSerializedString(cfAttr.value)] : [])
			const params = getDqParamsObject(dqParams, model)
			resolvers.push( //todo transpose and push all at once, merge result?
				from(searchDq({dqkey, params})).pipe(
					mergeMap((elements) => {
						//todo
						return [{
							paramsIndex: i,
							initValueFromDq: {[cfDef.id]: elements.map(x => x.value)},
						}]
					}),
					catchError((err) => {
						log.error('error in initValueFromDq dqkey=' + dqkey, dqParams, err)
						return []
					}),
				),
			)
		})
	})

	return [
		from(depActions),
		forkJoin(resolvers).pipe(
			map((x) => {
				log.debug('init cf model resolved with result:', x)
				const allInitValueFromDqMaps = x.reduce((m, x) => {
					if (isInitValueFromDqResult(x)) {
						const i = x.paramsIndex
						m[i] = {
							...m[i],
							...x.initValueFromDq,
						}
					}
					return m
				}, [])
				const stTagById = selStTagById(state$.value)
				const stTagZuById = selStTagZuById(state$.value)
				const kontStammByPersNr = selKontStammByPersNr(state$.value)
				const clipboard = selClipboardMap(state$.value)
				const urlParamsModel = selUrlParamsModel(state$.value)
				return params.map((p, i) => {
					const context: CFDefModelInitContext = {
						stTagZu: undefined,
						stTag: undefined,
						kontStamm: undefined,
						clipboard: clipboard,
						urlParams: urlParamsModel,
						initValueFromDqMap: allInitValueFromDqMaps[i],
					}
					const {persNr, date} = p
					if (persNr && date) {
						context.stTagZu = stTagZuById[`${persNr}_${date}`]
						context.stTag = context.stTagZu && stTagById[context.stTagZu.stTagKey]
						context.kontStamm = kontStammByPersNr[persNr]?.models.find(m => m.vondat <= date && m.bisdat >= date)
					}
					log.debug('init cf model context', p, context)
					return {
						...p,
						context,
					}
				})
			}),
		),
	]
}